import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';

import {FilterClause, GroupClause} from 'common/report/insights/util/types';
import {handleListReorder} from 'common/components/draggable-list/utils';
import {QueryFilterEntry, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {getPropertyFiltersFromBookmark} from 'iron/common/report/insights/util/filters';

import {BookmarkDragOperationProps} from './types';
import {generateFilter} from './utils';

function generateFilterFromBreakdown(breakdown: GroupClause, opts: {isCloning?: boolean} = {}) {
  const filter: Partial<FilterClause> = {
    ...omit(
      cloneDeep(breakdown),
      `cohorts`,
      `customBucket`,
      `datasetId`,
      `listItemGroup`,
      `joinPropertyType`,
      `propertyType`,
      `projectId`,
      `typeCast`,
      `unit`,
    ),

    dataGroupId: breakdown.dataGroupId,
    search: ``,
    dataset: ``,
    profileType: null,
    filterJoinType: breakdown.joinPropertyType,
    filterType: breakdown.typeCast || breakdown.propertyType!,
    defaultType: breakdown.propertyType,
    filterDateUnit: breakdown.unit,
  };

  // remove hidden or inherited fields from board filter/breakdown
  const forceClone = `isInherited` in breakdown && breakdown.isInherited;
  delete filter.isInherited;
  delete filter.isHidden;

  return {
    result: filter,
    forceClone: forceClone || opts.isCloning,
  };
}

/**
 * Handles rearranging the breakdown section
 */
export function handleBreakdownsRearrange(props: BookmarkDragOperationProps) {
  handleListReorder({
    event: props.event,
    entries: props.params.sections.group!,
    entryGenerator: (filter, opts) => generateFilter(filter, opts)?.result,
    setEntries: (updatedEntries) => {
      props.updateParams((draft) => {
        draft.sections.group = updatedEntries;
      });
    },
  });
}

/**
 * Handles moving a breakdown onto a filter section
 */
export function handleBreakdownToFilter(props: BookmarkDragOperationProps) {
  const breakdown = props.params.sections.group![props.sourceData.index];

  if (!props.targetData.setStagedEntry || !breakdown) {
    throw new Error(`Dropping filters requires a staging function`);
  }

  const filterDetail = generateFilterFromBreakdown(breakdown, {isCloning: props.isCloning});
  const propertyFilter = getPropertyFiltersFromBookmark([filterDetail.result])[0];

  const entry: QueryFilterEntry = {
    event: propertyFilter,
    type: SelectorTypeDefinitions.Filter,
  };

  // staged filters or bookmark updates do not exist at the report-editor level.
  // We have to keep them in the QB as we transition into a bookmark based QB
  props.targetData.setStagedEntry(entry);

  if (!filterDetail.forceClone) {
    props.updateParams((params) => {
      params.sections.group!.splice(props.sourceData.index, 1);
    });
  }
}
